<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import DriveAPI from 'dashboard/api/drive';

const props = defineProps({
  selectedFolder: {
    type: String,
    default: '/',
  },
});

const emit = defineEmits(['update']);

const { accountId } = useAccount();
const folders = ref([]);
const loading = ref(false);
const error = ref(null);

const availableFolders = computed(() => {
  const folderSet = new Set(['/']);

  folders.value.forEach(folder => {
    if (folder && folder !== '/') {
      // Ajouter le dossier et tous ses parents
      const parts = folder.split('/').filter(Boolean);
      let currentPath = '';
      parts.forEach(part => {
        currentPath += `/${part}`;
        folderSet.add(currentPath);
      });
    }
  });

  return Array.from(folderSet).sort();
});

const loadFolders = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await DriveAPI.getAll({});
    const allFiles = response.data || [];

    // Extraire tous les dossiers uniques
    const folderSet = new Set();
    allFiles.forEach(file => {
      if (file.folder_path && file.folder_path !== '/') {
        folderSet.add(file.folder_path);
      }
    });

    folders.value = Array.from(folderSet);
  } catch (err) {
    error.value =
      err.response?.data?.error ||
      err.response?.data?.message ||
      err.message ||
      'Erreur lors du chargement des dossiers';
    console.error('Erreur lors du chargement des dossiers:', err);
  } finally {
    loading.value = false;
  }
};

const selectFolder = folder => {
  emit('update', folder);
};

const getFolderDisplayName = folder => {
  if (folder === '/') return 'Racine (Tous les fichiers)';
  const parts = folder.split('/').filter(Boolean);
  return parts[parts.length - 1] || 'Racine';
};

onMounted(() => {
  loadFolders();
});
</script>

<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        Dossier racine accessible
      </label>
      <p class="text-xs text-n-slate-10 mb-3">
        Choisissez le dossier du Drive que les utilisateurs pourront voir et
        naviguer dans ce widget.
      </p>

      <div v-if="loading" class="flex items-center justify-center py-4">
        <span class="text-sm text-n-slate-10">Chargement des dossiers...</span>
      </div>

      <div
        v-else-if="error"
        class="p-3 bg-n-ruby-1 border border-n-ruby-6 rounded-lg"
      >
        <div class="flex items-center gap-2 text-sm text-n-ruby-11">
          <span class="i-lucide-alert-circle size-4" />
          <span>{{ error }}</span>
        </div>
      </div>

      <div
        v-else
        class="space-y-1 max-h-64 overflow-y-auto border border-n-weak rounded-lg p-2"
      >
        <button
          v-for="folder in availableFolders"
          :key="folder"
          class="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-n-alpha-2 transition-colors text-left"
          :class="
            selectedFolder === folder
              ? 'bg-n-blue-1 border border-n-blue-6'
              : ''
          "
          @click="selectFolder(folder)"
        >
          <div
            class="p-1.5 rounded flex-shrink-0"
            :class="selectedFolder === folder ? 'bg-n-blue-3' : 'bg-n-amber-2'"
          >
            <span
              class="size-4"
              :class="
                selectedFolder === folder
                  ? 'i-lucide-folder-open text-n-blue-11'
                  : 'i-lucide-folder text-n-amber-11'
              "
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-n-slate-12 truncate">
              {{ getFolderDisplayName(folder) }}
            </div>
            <div class="text-xs text-n-slate-10 truncate">
              {{ folder }}
            </div>
          </div>
          <span
            v-if="selectedFolder === folder"
            class="i-lucide-check size-4 text-n-blue-11 flex-shrink-0"
          />
        </button>
      </div>

      <button
        class="mt-2 text-xs text-n-blue-11 hover:text-n-blue-12 hover:underline flex items-center gap-1"
        @click="loadFolders"
      >
        <span class="i-lucide-refresh-cw size-3" />
        Actualiser la liste
      </button>
    </div>
  </div>
</template>
